import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from "../../../environments/environment";
import { APIResponseWrapper } from "../../models/common/api-response-wrapper";
import { Vehicle } from "./vehicle.model";
import { APIListWrapper } from "../../models/common/api-list-wrapper.model";
import { Driver } from "../driver/driver.model";
import { VehicleMin } from "./vehicle-min.model";

export interface RentalResourceTerms {
    vendorName?: string;
    vendorCompanyId?: number | null;
    contractReferenceNumber?: string;
    rentalStartDate?: number | Date | null;
    rentalEndDate?: number | Date | null;
    rentalRateAmount?: number | null;
    rentalRateCurrency?: string;
    rentalRatePeriod?: string;
    depositAmount?: number | null;
    depositCurrency?: string;
    renewalStatus?: string;
    billId?: number | null;
    expenseId?: number | null;
    notes?: string;
}

@Injectable({
    providedIn: 'root'
})
export class VehicleService {

    private baseUrl = `${environment.restApiUrl}vehicle-endpoint`; // Adjust the base URL if needed

    constructor(private http: HttpClient) { }

    findAllGoodsInsuranceCompanies(countryId: number, cultureCode: string) { // Adjust return type if needed
        const params = { countryId: countryId.toString(), cultureCode };
        return this.http.get(`${this.baseUrl}/goods-insurance-companies`, { params });
    }

    findVehicleByIds(cultureCode: string, vehicleIds: number[], companyId?: number, companyGroupId?: number) {
        const params: any = { cultureCode, vehicleIds: vehicleIds.join(',') };
        if (companyId != null) {
            params.companyId = companyId.toString();
        }
        if (companyGroupId != null) {
            params.companyGroupId = companyGroupId.toString();
        }
        return this.http.get<APIResponseWrapper<Vehicle[]>>(`${this.baseUrl}/vehicles`, { params });
    }

    findVehicleByQuery(cultureCode: string, registrationNumber?: string, plateNumber?: string) {
        const params: any = {};
        params.cultureCode = cultureCode
        if (registrationNumber)
            params.registrationNumber = registrationNumber;
        if (plateNumber)
            params.plateNumber = plateNumber;
        return this.http.get<APIResponseWrapper<Vehicle>>(`${this.baseUrl}/vehicles/search`, { params });
    }

    addVehicle(vehicle: Vehicle) {
        return this.http.post<APIResponseWrapper<Vehicle>>(`${this.baseUrl}/vehicles`, vehicle);
    }

    updateVehicle(vehicle: Vehicle) {
        return this.http.put<APIResponseWrapper<Vehicle>>(`${this.baseUrl}/vehicles`, vehicle);
    }

    updateVehicleActiveStatus(vehicleId: number, active: boolean) {
        const params = { active: active.toString() };
        return this.http.patch<APIResponseWrapper<Vehicle>>(`${this.baseUrl}/vehicles/${vehicleId}/active`, null, { params });
    }

    deleteVehicle(vehicle: Vehicle) { // Adjust return type if needed
        return this.http.delete(`${this.baseUrl}/vehicles`, { body: vehicle });
    }

    findVehiclesByTransportationAgencyId(page: number, pageSize: number, transportationAgencyId: number) {
        const params = { page: page.toString(), pageSize: pageSize.toString(), transportationAgencyId: transportationAgencyId.toString() };
        return this.http.get<APIListWrapper<Vehicle>>(`${this.baseUrl}/vehicles-by-transportation-agency`, { params });
    }

    findVehiclesByCompanyGroup(page: number, pageSize: number, companyGroupId: number, excludeCompanyId: number, memberCompanyId?: number | null) {
        const params: any = {
            page: page.toString(),
            pageSize: pageSize.toString(),
            companyGroupId: companyGroupId.toString(),
            excludeCompanyId: excludeCompanyId.toString()
        };
        if (memberCompanyId != null) {
            params.memberCompanyId = memberCompanyId.toString();
        }
        return this.http.get<APIListWrapper<Vehicle>>(`${this.baseUrl}/vehicles-by-company-group`, { params });
    }

    getFreelanceVehiclesByCompanyId(page: number, pageSize: number, companyId: number) {
        const params = { page: page.toString(), pageSize: pageSize.toString(), companyId: companyId.toString() };
        return this.http.get<APIListWrapper<Vehicle>>(`${this.baseUrl}/freelance-vehicles-by-company`, { params });
    }

    findEligibleFreelanceVehicleByCompanyId(vehicleId: number, companyId: number) {
        const params = { companyId: companyId.toString() };
        return this.http.get<APIResponseWrapper<Vehicle | null>>(
            `${this.baseUrl}/freelance-vehicles-by-company/${vehicleId}`, { params });
    }

    getFreelanceVehiclesByCompanyGroup(page: number, pageSize: number, companyGroupId: number, excludeCompanyId: number, memberCompanyId?: number | null) {
        const params: any = {
            page: page.toString(),
            pageSize: pageSize.toString(),
            companyGroupId: companyGroupId.toString(),
            excludeCompanyId: excludeCompanyId.toString()
        };
        if (memberCompanyId != null) {
            params.memberCompanyId = memberCompanyId.toString();
        }
        return this.http.get<APIListWrapper<Vehicle>>(`${this.baseUrl}/freelance-vehicles-by-company-group`, { params });
    }

    findVehicleMinsByTransportationAgencyId(transportationAgencyId: number) {
        const params = { transportationAgencyId: transportationAgencyId.toString() };
        return this.http.get<APIListWrapper<VehicleMin>>(`${this.baseUrl}/vehicle-mins-by-transportation-agency`, { params });
    }

    getFreelanceVehicleMinsByCompanyId(companyId: number) {
        const params = { companyId: companyId.toString() };
        return this.http.get<APIListWrapper<VehicleMin>>(`${this.baseUrl}/freelance-vehicle-mins-by-company`, { params });
    }

    addFreelancerVehicle(vehicle: Vehicle, companyId: number) {
        const url = `${this.baseUrl}/freelance-vehicle?companyId=${companyId}`;
        return this.http.post<APIResponseWrapper<Vehicle>>(url, vehicle);
    }

    getTransporterVehicles(page: number, pageSize: number, companyId: number, transporterCrmClientId: number) {
        const params = {
            page: page.toString(),
            pageSize: pageSize.toString(),
            companyId: companyId.toString(),
            transporterCrmClientId: transporterCrmClientId.toString()
        };
        return this.http.get<APIListWrapper<Vehicle>>(`${this.baseUrl}/transporter-vehicles`, { params });
    }

    addTransporterVehicle(vehicle: Vehicle, companyId: number) {
        return this.http.post<APIResponseWrapper<Vehicle>>(`${this.baseUrl}/transporter-vehicle?companyId=${companyId}`, vehicle);
    }

    getRentalVehiclesByCompanyId(page: number, pageSize: number, companyId: number) {
        const params = { page: page.toString(), pageSize: pageSize.toString(), companyId: companyId.toString() };
        return this.http.get<APIListWrapper<Vehicle>>(`${this.baseUrl}/rental-vehicles-by-company`, { params });
    }

    getRentalVehiclesByCompanyGroup(page: number, pageSize: number, companyGroupId: number, excludeCompanyId: number, memberCompanyId?: number | null) {
        const params: any = {
            page: page.toString(),
            pageSize: pageSize.toString(),
            companyGroupId: companyGroupId.toString(),
            excludeCompanyId: excludeCompanyId.toString()
        };
        if (memberCompanyId != null) {
            params.memberCompanyId = memberCompanyId.toString();
        }
        return this.http.get<APIListWrapper<Vehicle>>(`${this.baseUrl}/rental-vehicles-by-company-group`, { params });
    }

    getRentalVehicleMinsByCompanyId(companyId: number) {
        const params = { companyId: companyId.toString() };
        return this.http.get<APIListWrapper<VehicleMin>>(`${this.baseUrl}/rental-vehicle-mins-by-company`, { params });
    }

    addRentalVehicle(vehicle: Vehicle, companyId: number, terms: RentalResourceTerms) {
        const url = `${this.baseUrl}/rental-vehicle?companyId=${companyId}`;
        return this.http.post<APIResponseWrapper<Vehicle>>(url, { vehicle, ...terms });
    }

    deactivateRentalVehicle(vehicleId: number, companyId: number) {
        const params = { vehicleId: vehicleId.toString(), companyId: companyId.toString() };
        return this.http.delete<APIResponseWrapper<boolean>>(`${this.baseUrl}/rental-vehicle`, { params });
    }
}
